import { useEffect, useState } from "react";
import {
  ANLASS_LABEL,
  type QualitaetsGateModus,
  type QualitaetsGateSchwelle,
  type Rolle,
} from "@cubic/shared";
import { Button } from "@/components/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/Dialog";
import { Fehlerzeile } from "@/components/Fehlerzeile";
import { Label } from "@/components/Label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/Select";
import { Mikrolabel } from "@/components/entwurf/Mikrolabel";
import { Pruefquadrat } from "@/components/entwurf/Pruefquadrat";
import { Rubrik } from "@/components/entwurf/Rubrik";
import { Schalter } from "@/components/entwurf/Schalter";
import {
  fehlerMeldung,
  useAutoQaAutomationen,
  useIch,
  useQualitaetsGateRegel,
  useQualitaetsGateRegelAendern,
} from "@/lib/haken";
import {
  QUALITAETS_GATE_MODUS_TEXT,
  qualitaetsGateRegelFehler,
} from "@/lib/qualitaets-gate-zustand";
import { darfQualitaetsGateAendern } from "@/lib/rollen";

type RegelEntwurf = {
  modus: QualitaetsGateModus;
  pipelineErforderlich: boolean;
  autoQaErforderlich: boolean;
  schwereGrenze: QualitaetsGateSchwelle;
  automationIds: readonly string[];
};

const MODUS_ERKLAERUNG: Readonly<Record<QualitaetsGateModus, string>> = {
  aus: "Es wird kein Gate-Ergebnis erhoben; Merge Requests bleiben unberührt.",
  schatten:
    "Das Gate bewertet und zeigt, was blockieren würde, sperrt den Merge aber nicht.",
  blockierend:
    "Ein Merge bleibt gesperrt, bis alle Pflichtsignale bestehen oder die aktuelle Prüfung begründet übersteuert wird.",
};

function alsEntwurf(regel: RegelEntwurf): RegelEntwurf {
  return { ...regel, automationIds: [...regel.automationIds] };
}

function gleicheRegel(a: RegelEntwurf, b: RegelEntwurf): boolean {
  return (
    a.modus === b.modus &&
    a.pipelineErforderlich === b.pipelineErforderlich &&
    a.autoQaErforderlich === b.autoQaErforderlich &&
    a.schwereGrenze === b.schwereGrenze &&
    [...a.automationIds].sort().join("\u0000") ===
      [...b.automationIds].sort().join("\u0000")
  );
}

function automationZustand(zustand: string): string {
  if (zustand === "aktiv") return "Aktiv";
  if (zustand === "pausiert") return "Pausiert";
  if (zustand === "entwurf") return "Entwurf";
  return zustand;
}

function automationAnlass(anlass: string): string {
  return (ANLASS_LABEL as Readonly<Record<string, string>>)[anlass] ?? anlass;
}

export function QualitaetsGateEinstellungen({
  projektId,
  rolle,
}: {
  projektId: string;
  rolle: Rolle | undefined;
}) {
  const regel = useQualitaetsGateRegel(projektId);
  const automationen = useAutoQaAutomationen(projektId);
  const ich = useIch();
  const speichern = useQualitaetsGateRegelAendern(projektId);
  const [entwurf, setzeEntwurf] = useState<RegelEntwurf | null>(null);
  const [entwurfVersion, setzeEntwurfVersion] = useState<number | null>(null);
  const [bestaetigungOffen, setzeBestaetigungOffen] = useState(false);
  const [gespeichert, setzeGespeichert] = useState(false);

  useEffect(() => {
    if (!regel.data || entwurfVersion === regel.data.version) return;
    setzeEntwurf(alsEntwurf(regel.data));
    setzeEntwurfVersion(regel.data.version);
    setzeBestaetigungOffen(false);
  }, [entwurfVersion, regel.data]);

  if (regel.isLoading || (regel.data && !entwurf)) {
    return (
      <div className="mt-10" aria-busy="true">
        <Rubrik>Quality Gate</Rubrik>
        <Mikrolabel grad={11.5} className="mt-4 block">
          Lade Gate-Regel …
        </Mikrolabel>
      </div>
    );
  }

  if (!regel.data || !entwurf) {
    return (
      <div className="mt-10">
        <Rubrik>Quality Gate</Rubrik>
        <div className="mt-4">
          <Fehlerzeile
            meldung={fehlerMeldung(regel.error)}
            onErneut={() => void regel.refetch()}
          />
        </div>
      </div>
    );
  }

  const ausgang = alsEntwurf(regel.data);
  const kannAendern = darfQualitaetsGateAendern(rolle, {
    rechte: ich.data?.rechte ?? [],
  });
  const formFehler = qualitaetsGateRegelFehler(entwurf);
  const geaendert = !gleicheRegel(ausgang, entwurf);

  const aendere = (aenderung: Partial<RegelEntwurf>) => {
    setzeEntwurf((aktuell) =>
      aktuell ? { ...aktuell, ...aenderung } : aktuell,
    );
    setzeGespeichert(false);
    speichern.reset();
  };

  const sende = () => {
    if (formFehler) return;
    speichern.mutate(
      {
        ...entwurf,
        automationIds: [...entwurf.automationIds],
        erwarteteVersion: regel.data.version,
      },
      {
        onSuccess: () => {
          setzeBestaetigungOffen(false);
          setzeGespeichert(true);
        },
      },
    );
  };

  const speichernAnfragen = () => {
    if (formFehler || !geaendert) return;
    if (
      regel.data.modus !== "blockierend" &&
      entwurf.modus === "blockierend"
    ) {
      setzeBestaetigungOffen(true);
      return;
    }
    sende();
  };

  const ausgewaehlteNamen = entwurf.automationIds.map(
    (id) => automationen.data?.find((automation) => automation.id === id)?.name ?? id,
  );

  return (
    <div className="mt-10">
      <Rubrik
        anzahl={
          regel.data.istVorgabe
            ? "Sichere Vorgabe"
            : `Fassung ${regel.data.version}`
        }
      >
        Quality Gate
      </Rubrik>
      <p className="mt-4 max-w-[62ch] text-[13.5px] font-light leading-[1.65] text-tinte3">
        Legt fest, welche Pipeline- und Auto-Q&A-Signale einen Merge Request
        beobachten oder tatsächlich aufhalten.
      </p>

      {!kannAendern ? (
        <div className="mt-5">
          <dl>
            <div className="flex items-baseline gap-6 border-b border-linie py-3">
              <dt className="w-[124px] flex-none">
                <Mikrolabel grad={11.5}>Modus</Mikrolabel>
              </dt>
              <dd className="text-[13.5px] text-tinte">
                {QUALITAETS_GATE_MODUS_TEXT[entwurf.modus]}
              </dd>
            </div>
            <div className="flex items-baseline gap-6 border-b border-linie py-3">
              <dt className="w-[124px] flex-none">
                <Mikrolabel grad={11.5}>Pflichtsignale</Mikrolabel>
              </dt>
              <dd className="text-[13.5px] text-tinte">
                {[
                  entwurf.pipelineErforderlich ? "Pipeline" : null,
                  entwurf.autoQaErforderlich ? "Auto Q&A" : null,
                ]
                  .filter(Boolean)
                  .join(" · ") || "Keine"}
              </dd>
            </div>
            <div className="flex items-baseline gap-6 border-b border-linie py-3">
              <dt className="w-[124px] flex-none">
                <Mikrolabel grad={11.5}>Befundgrenze</Mikrolabel>
              </dt>
              <dd className="text-[13.5px] text-tinte">
                Ab {entwurf.schwereGrenze}
              </dd>
            </div>
            <div className="flex items-start gap-6 border-b border-linie py-3">
              <dt className="w-[124px] flex-none">
                <Mikrolabel grad={11.5}>Automationen</Mikrolabel>
              </dt>
              <dd className="min-w-0 text-[13.5px] text-tinte">
                {ausgewaehlteNamen.join(" · ") || "Keine ausgewählt"}
              </dd>
            </div>
          </dl>
          <Mikrolabel grad={11.5} className="mt-4 block">
            Ändern können Projektverwaltende mit dem Recht
            „gitlab.konfigurieren“.
          </Mikrolabel>
        </div>
      ) : (
        <>
          <div className="mt-6">
            <Label htmlFor="quality-gate-modus">Modus</Label>
            <Select
              value={entwurf.modus}
              disabled={speichern.isPending}
              onValueChange={(wert) =>
                aendere({ modus: wert as QualitaetsGateModus })
              }
            >
              <SelectTrigger id="quality-gate-modus" className="mt-1.5 w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="aus">Aus</SelectItem>
                <SelectItem value="schatten">Beobachtung</SelectItem>
                <SelectItem value="blockierend">Blockierend</SelectItem>
              </SelectContent>
            </Select>
            <Mikrolabel grad={11} className="mt-2 block">
              {MODUS_ERKLAERUNG[entwurf.modus]}
            </Mikrolabel>
          </div>

          <div className="mt-6">
            <Mikrolabel grad={11.5} ton="laut" betont>
              Pflichtsignale
            </Mikrolabel>
            <div className="mt-1">
              <div className="flex min-h-[52px] items-center gap-4 border-b border-linie py-2">
                <div className="min-w-0 flex-1">
                  <p className="text-[13.5px] text-tinte">Pipeline</p>
                  <Mikrolabel grad={10.5} className="mt-0.5 block">
                    Muss zur aktuellen Kopf-SHA erfolgreich sein.
                  </Mikrolabel>
                </div>
                <Schalter
                  an={entwurf.pipelineErforderlich}
                  disabled={speichern.isPending}
                  onKlick={() =>
                    aendere({
                      pipelineErforderlich: !entwurf.pipelineErforderlich,
                    })
                  }
                  ariaLabel="Pipeline als Pflichtsignal verwenden"
                />
              </div>
              <div className="flex min-h-[52px] items-center gap-4 border-b border-linie py-2">
                <div className="min-w-0 flex-1">
                  <p className="text-[13.5px] text-tinte">Auto Q&amp;A</p>
                  <Mikrolabel grad={10.5} className="mt-0.5 block">
                    Ausgewählte Automationen müssen dieselbe Revision vollständig prüfen.
                  </Mikrolabel>
                </div>
                <Schalter
                  an={entwurf.autoQaErforderlich}
                  disabled={speichern.isPending}
                  onKlick={() =>
                    aendere({ autoQaErforderlich: !entwurf.autoQaErforderlich })
                  }
                  ariaLabel="Auto Q&A als Pflichtsignal verwenden"
                />
              </div>
            </div>
          </div>

          <div className="mt-6">
            <Label htmlFor="quality-gate-schwere">Befundgrenze</Label>
            <Select
              value={entwurf.schwereGrenze}
              onValueChange={(wert) =>
                aendere({ schwereGrenze: wert as QualitaetsGateSchwelle })
              }
              disabled={!entwurf.autoQaErforderlich || speichern.isPending}
            >
              <SelectTrigger
                id="quality-gate-schwere"
                className="mt-1.5 w-full"
                disabled={!entwurf.autoQaErforderlich || speichern.isPending}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="schwer">Schwer und kritisch</SelectItem>
                <SelectItem value="kritisch">Nur kritisch</SelectItem>
              </SelectContent>
            </Select>
            <Mikrolabel grad={11} className="mt-2 block">
              Offene Befunde ab dieser Schwere halten ein blockierendes Gate auf.
            </Mikrolabel>
          </div>

          <div className="mt-6">
            <div className="flex items-baseline justify-between gap-4">
              <Mikrolabel grad={11.5} ton="laut" betont>
                Auto-Q&amp;A-Automationen
              </Mikrolabel>
              <Mikrolabel grad={10.5} zahl>
                {entwurf.automationIds.length} von 20
              </Mikrolabel>
            </div>
            {automationen.isLoading ? (
              <Mikrolabel grad={11} className="mt-3 block">
                Lade Automationen …
              </Mikrolabel>
            ) : automationen.isError ? (
              <div className="mt-3">
                <Fehlerzeile
                  meldung={fehlerMeldung(automationen.error)}
                  onErneut={() => void automationen.refetch()}
                />
              </div>
            ) : automationen.data?.length ? (
              <ul className="mt-1" role="list">
                {automationen.data.map((automation) => {
                  const ausgewaehlt = entwurf.automationIds.includes(
                    automation.id,
                  );
                  const amLimit =
                    !ausgewaehlt && entwurf.automationIds.length >= 20;
                  return (
                    <li
                      key={automation.id}
                      className="flex min-h-[52px] items-center gap-4 border-b border-linie py-2"
                    >
                      <Pruefquadrat
                        erfuellt={ausgewaehlt}
                        disabled={amLimit || speichern.isPending}
                        ariaLabel={`${automation.name} ${
                          ausgewaehlt
                            ? "abwählen"
                            : amLimit
                              ? "kann wegen der Höchstzahl nicht ausgewählt werden"
                              : "auswählen"
                        }`}
                        onKlick={
                          amLimit
                            ? undefined
                            : () =>
                                aendere({
                                  automationIds: ausgewaehlt
                                    ? entwurf.automationIds.filter(
                                        (id) => id !== automation.id,
                                      )
                                    : [
                                        ...entwurf.automationIds,
                                        automation.id,
                                      ],
                                })
                        }
                      />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-[13.5px] text-tinte">
                          {automation.name}
                        </p>
                        <Mikrolabel grad={10.5} className="mt-0.5 block">
                          {automationZustand(automation.zustand)} ·{" "}
                          {automationAnlass(automation.anlass)}
                        </Mikrolabel>
                      </div>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="mt-3 bg-wisch px-4 py-3 text-[12.5px] text-tinte3">
                Für dieses Projekt gibt es noch keine Auto-Q&amp;A-Automation.
              </p>
            )}
          </div>

          {formFehler ? (
            <p
              className="mt-5 border-l-2 border-warn bg-warnflaeche px-3.5 py-2.5 text-[12.5px] text-tinte"
              role="alert"
            >
              {formFehler}
            </p>
          ) : null}

          {speichern.error ? (
            <div className="mt-5">
              <Fehlerzeile
                meldung={fehlerMeldung(speichern.error)}
                onNeuLaden={() => void regel.refetch()}
                onErneut={sende}
              />
            </div>
          ) : null}

          <div className="mt-5 flex flex-wrap items-center gap-3">
            <Button
              onClick={speichernAnfragen}
              disabled={!geaendert || Boolean(formFehler)}
              isLoading={speichern.isPending}
              loadingText="Speichert …"
            >
              Gate-Regel speichern
            </Button>
            <span aria-live="polite">
              {gespeichert ? (
                <Mikrolabel grad={11}>Gespeichert.</Mikrolabel>
              ) : null}
            </span>
          </div>
        </>
      )}

      <Dialog open={bestaetigungOffen} onOpenChange={setzeBestaetigungOffen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Blockierendes Quality Gate einschalten?</DialogTitle>
            <DialogDescription>
              Ab dem Speichern werden Merge Requests dieses Projekts gesperrt,
              sobald ein Pflichtsignal fehlt, veraltet ist, scheitert oder
              blockierende Befunde enthält.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-5 bg-warnflaeche px-3.5 py-3 text-[12.5px] leading-relaxed text-tinte">
            Pflicht: {entwurf.pipelineErforderlich ? "Pipeline" : "keine Pipeline"}
            {entwurf.autoQaErforderlich
              ? ` · ${entwurf.automationIds.length} Auto-Q&A-Automation${
                  entwurf.automationIds.length === 1 ? "" : "en"
                } · Befunde ab ${entwurf.schwereGrenze}`
              : " · kein Auto Q&A"}
          </div>
          <DialogFooter className="mt-5">
            <Button
              variant="secondary"
              onClick={() => setzeBestaetigungOffen(false)}
            >
              Abbrechen
            </Button>
            <Button
              variant="destructive"
              onClick={sende}
              isLoading={speichern.isPending}
              loadingText="Schaltet ein …"
            >
              Blockierend einschalten
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
